/**
 * ProofGalleryCard — single proof rendered as a vault-style card,
 * used in the 5-card recent gallery on the Home page.
 *
 * Hovers via the motion library's `lift` verb. On `celebrate=true`
 * (post-first-proof moment), arrives via `settle` instead of plain
 * `arrive` and gets an emerald top-edge sweep + 2px ring.
 *
 * Per [docs/DASHBOARD-HOME-EXPLORE-IMPLEMENTATION.md §2.4].
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CheckCircle2,
  FileText,
  Lightbulb,
  Receipt,
  FileSignature,
  Mail,
  type LucideIcon,
} from 'lucide-react';

import type { Proof } from '@/api/proofs';
import type { ProofKind } from '@/stores/proofsStore';
import { shortHash } from '@/utils/hashing';
import { settle, arrive, lift } from '@/lib/motion';
import { cn } from '@/utils/cn';

const ICON_BY_KIND: Record<ProofKind, LucideIcon> = {
  file: FileText,
  idea: Lightbulb,
  invoice: Receipt,
  contract: FileSignature,
  memo: Mail,
  document: FileText,
};

export interface ProofGalleryCardProps {
  proof: Proof;
  /** True for the topmost card on Home immediately after the user
   *  signs their first proof. Triggers `settle` + emerald sweep. */
  celebrate?: boolean;
}

export function ProofGalleryCard({ proof, celebrate }: ProofGalleryCardProps) {
  const { t, i18n } = useTranslation('dashboard');
  const reduce = useReducedMotion();
  const Icon = ICON_BY_KIND[proof.kind] ?? FileText;
  const variants = celebrate ? settle : arrive;

  return (
    <motion.div
      className="relative"
      {...(reduce ? {} : { variants, whileHover: lift })}
    >
      {/* Emerald progress-bar sweep on celebrate. */}
      {celebrate && !reduce ? <CelebrationSweep /> : null}

      <Link
        to={`/vault/${proof.id}`}
        className={cn(
          'group flex h-full flex-col rounded-xl border border-border bg-surface p-4 shadow-subtle',
          'transition-shadow duration-150 ease-emphasized',
          'hover:shadow-raised hover:border-border-strong',
          celebrate &&
            'ring-2 ring-success/40 ring-offset-2 ring-offset-canvas',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-spark-soft text-spark"
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            {t('recent.signed_label')}
          </span>
        </div>

        <p className="mt-3 truncate text-sm font-semibold text-ink">
          {proof.name}
        </p>
        <code className="mt-1 truncate font-mono text-[11px] text-ink-subtle">
          {shortHash(proof.hash, 6, 4)}
        </code>
        <p className="mt-3 text-[11px] text-ink-muted">
          {new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(proof.timestamp))}
        </p>
      </Link>
    </motion.div>
  );
}

/** A 600ms emerald bar that sweeps left→right across the top of
 *  the card on first-proof celebration, then fades. */
function CelebrationSweep() {
  return (
    <motion.span
      aria-hidden
      className="pointer-events-none absolute start-0 top-0 h-0.5 w-full origin-left bg-success"
      initial={{ scaleX: 0, opacity: 1 }}
      animate={{ scaleX: 1, opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.2, 0, 0, 1] }}
    />
  );
}
